import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { userApi } from "@/lib/userApi";
import { useUserAuth } from "@/context/UserAuthContext";
import { CheckCircle2, Loader2, XCircle, Coins } from "lucide-react";
import { NoirBg } from "@/components/NoirBg";
import { GlassButton } from "@/components/GlassBits";

const MAX_TRIES = 8;

/** Vuelta desde Stripe: confirma el pago y muestra los créditos abonados. */
export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [credits, setCredits] = useState(null);
  const { refresh } = useUserAuth();
  const tries = useRef(0);
  const nav = useNavigate();

  useEffect(() => {
    if (cancelled || !sessionId) {
      if (!cancelled && !sessionId) setState("error");
      return;
    }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await userApi.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setCredits(data.credits);
          setState("paid");
          await refresh();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) return setState("error");
      } catch (e) {
        return setState("error");
      }
      tries.current += 1;
      if (tries.current >= MAX_TRIES) return setState("pending");
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, cancelled, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center theme-noir px-6"
      style={{ backgroundColor: "#05070C" }} data-testid="payment-result">
      <NoirBg />
      <div className="glass-card forge-card p-8 max-w-md w-full text-center relative">
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-amber-300 mx-auto" />
            <h1 className="text-white font-heading text-xl mt-5">Confirmando tu pago…</h1>
            <p className="text-sm text-white/60 mt-2">No cierres esta ventana.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
            <h1 className="text-white font-heading text-2xl mt-5" data-testid="payment-success-title">
              ¡Pago confirmado!
            </h1>
            <p className="text-sm text-white/70 mt-2 flex items-center justify-center gap-2">
              <Coins className="w-4 h-4 text-amber-300" /> Se abonaron {credits} créditos a tu cuenta.
            </p>
            <div className="mt-6">
              <GlassButton tone="red" data-testid="go-panel-btn" onClick={() => nav("/panel")}>
                Ir a mi panel
              </GlassButton>
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-amber-300 mx-auto" />
            <h1 className="text-white font-heading text-xl mt-5">Tu pago sigue procesándose</h1>
            <p className="text-sm text-white/60 mt-2">
              En cuanto el banco lo confirme, los créditos aparecen solos en tu panel.
            </p>
            <div className="mt-6">
              <GlassButton tone="neutral" onClick={() => nav("/panel")}>Ir a mi panel</GlassButton>
            </div>
          </>
        )}
        {(state === "cancelled" || state === "error") && (
          <>
            <XCircle className="w-12 h-12 text-rose-400 mx-auto" />
            <h1 className="text-white font-heading text-2xl mt-5" data-testid="payment-cancel-title">
              {state === "cancelled" ? "Pago cancelado" : "No pudimos confirmar el pago"}
            </h1>
            <p className="text-sm text-white/60 mt-2">
              No se te cobró nada. Puedes intentarlo otra vez desde tu panel.
            </p>
            <div className="mt-6">
              <GlassButton tone="red" data-testid="retry-payment-btn" onClick={() => nav("/panel")}>
                Volver a intentar
              </GlassButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
